// src/screens/TradeListScreen.js - REVISED & RESTRUCTURED

import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../firebase'; // Import db directly
// ✨ NEW: Assuming a central auth hook exists
import { useAuth } from '../hooks/useAuth';
// ✨ NEW: Imports for router and data hooks
import { useLocation, useNavigate } from 'react-router-dom';
import { useUserItems } from '../hooks/useUserItems'; // Our new hook for fetching user's items
import { useTradeActions } from '../hooks/useTradeActions';
import { useSmartNotifications, useTabNotifications } from '../hooks/useSmartNotifications';
//import { useSmartNotifications, useTabNotifications } from '../hooks/useSmartNotifications';
// --- Component Imports ---
import ItemList from '../components/ItemList';
import TradesList from '../components/TradesList';
import RatingModal from '../components/RatingModal';
import { TabNotificationIndicator } from '../components/SmartNotificationDot';
import LoadingScreen from './LoadingScreen'; // For a better loading experience
// ✨ NEW: Business logic is now in its own hook
// import { useTradeActions } from '../hooks/useTradeActions'; 

// ✨ NEW: Components are imported from their own files
// import ItemList from '../components/ItemList'; 
// import TradesList from '../components/TradesList';
// import RatingModal from '../components/RatingModal';
// import { TabNotificationIndicator } from '../components/SmartNotificationDot';

//export default function TradeListScreen({ db, navigate, setModal, userItems = [], route }) {
// ✨ CHANGED: Simplified props
export default function TradeListScreen({ setModal }) {  
// ✨ CLEANER: Profile comes from a central hook. Handles loading and suspended states.
    const { profile, loading: profileLoading } = useAuth();
    const { userItems, loading: userItemsLoading } = useUserItems(); // Fetch user items with our hook
    const navigate = useNavigate(); // Get navigate from the router hook
    const location = useLocation(); // Get location to read state

    const userId = profile?.id;

    const [allTrades, setAllTrades] = useState([]);
    const [loadingTrades, setLoadingTrades] = useState(true);
    const [ratingTrade, setRatingTrade] = useState(null);

    // ✨ CLEANER: Actions are destructured from the custom hook
    const { confirmShipment, confirmReceipt, handleSubmitRating: submitRatingAction, openChatForTrade } = useTradeActions(db, userId, setModal, navigate);

    const { notifications: globalNotifications, createNotification } = useSmartNotifications(db, userId);
    const { notifications: buyerNotifications } = useTabNotifications(db, userId, 'buyer');
    const { notifications: sellerNotifications } = useTabNotifications(db, userId, 'seller');
    const { notifications: statusNotifications } = useTabNotifications(db, userId, 'status');
    
    // const [activeTab, setActiveTab] = useState(route?.params?.initialTab || 'items');
    // ✨ CHANGED: Read initialTab from router location state
    const [activeTab, setActiveTab] = useState(location.state?.initialTab || 'items');  
    const [pagination, setPagination] = useState({ items: 1, buyer: 1, seller: 1, status: 1 });

    // Effect to fetch trades remains the same
    useEffect(() => {
        if (!userId) {
            setLoadingTrades(false);
            return;
        }
        setLoadingTrades(true);
        const q = query(collection(db, "trades"), where("participants", "array-contains", userId), orderBy("createdAt", "desc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setAllTrades(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoadingTrades(false);
        }, (error) => {
            console.error("Error loading trades:", error);
            setModal?.({ message: "Erro ao carregar transações." });
            setLoadingTrades(false);
        });
        return () => unsubscribe();
    }, [userId, setModal]);

    // Memoized lists for each tab, remains the same
    const deliveredTrades = useMemo(() => allTrades.filter(t => t.sellerId === userId && t.status.includes('completed')), [allTrades, userId]);
    const receivedTrades = useMemo(() => allTrades.filter(t => t.buyerId === userId && t.status.includes('completed')), [allTrades, userId]);
    const statusTrades = useMemo(() => allTrades.filter(t => !t.status.includes('completed') && !t.status.includes('cancelled')), [allTrades]);

    const handlePageChange = (tab, newPage) => setPagination(prev => ({ ...prev, [tab]: newPage }));

    const handleSubmitRating = async (formData) => {
	    {/*  await submitRating(formData, ratingTrade);
        setRatingTrade(null); */} // Close modal on success
            if (typeof submitRatingAction !== 'function') {
            console.error("submitRatingAction is not a function. Check useTradeActions exports.");
            return;
        }

        try {
            // formData usually contains { rating, comment } from RatingModal
            await submitRatingAction(formData); 
            setRatingTrade(null); 
        } catch (err) {
            console.error("Rating error:", err);
        }
    };
    
    const getUserToRate = (trade) => {
        if (!trade || !userId) return null;
        const otherUserId = userId === trade.buyerId ? trade.sellerId : trade.buyerId;
        return trade.participantInfo?.find(p => p.id === otherUserId);
    };

    // ✨ CHANGED: Unified loading state
    if (profileLoading || loadingTrades || userItemsLoading) {
        return <LoadingScreen />;
    }

    // Security check remains important
    if (profile?.suspended) {
        navigate('/home');
        setModal({ title: "Conta Suspensa", message: "Sua conta está suspensa." });
        return null;
    }

    const renderContent = () => {
        switch (activeTab) {
            case 'items':
                return <ItemList items={userItems} navigate={navigate} currentPage={pagination.items} onPageChange={(p) => handlePageChange('items', p)} notifications={globalNotifications} />;
            case 'status':
                return <TradesList trades={statusTrades} navigate={navigate} userId={userId} currentPage={pagination.status} onPageChange={(p) => handlePageChange('status', p)} onConfirmShipment={(trade) => confirmShipment(trade, createNotification)} onConfirmReceived={confirmReceipt} onOpenChat={openChatForTrade} activeTab={activeTab} notifications={statusNotifications}/>;
            case 'buyer':
                return <TradesList trades={receivedTrades} navigate={navigate} userId={userId}  currentPage={pagination.buyer} onPageChange={(p) => handlePageChange('buyer', p)} onOpenRatingModal={setRatingTrade} activeTab={activeTab} notifications={buyerNotifications} />;
            case 'seller':
                return <TradesList trades={deliveredTrades} navigate={navigate} userId={userId} currentPage={pagination.seller} onPageChange={(p) => handlePageChange('seller', p)} onOpenRatingModal={setRatingTrade} activeTab={activeTab} notifications={sellerNotifications} navigate={navigate} />;
            default:
                return null;
        }
    };
    
    return (
        <div className="max-w-4xl mx-auto p-2 sm:p-4">
            {ratingTrade && <RatingModal onClose={() => setRatingTrade(null)} onSubmit={handleSubmitRating} userToRate={getUserToRate(ratingTrade)} />}
            
            <h1 className="text-2xl sm:text-3xl font-bold mb-6">Minhas Transações</h1>

            <div className="border-b mb-4">
                <button onClick={() => setActiveTab('items')} className={`relative py-2 px-4 font-semibold ${activeTab === 'items' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}>Anúncios <TabNotificationIndicator notifications={globalNotifications} /></button>
                <button onClick={() => setActiveTab('status')} className={`relative py-2 px-4 font-semibold ${activeTab === 'status' ? 'border-b-2 border-orange-600 text-orange-600' : 'text-gray-500'}`}>Status <TabNotificationIndicator notifications={statusNotifications} /></button>
                <button onClick={() => setActiveTab('buyer')} className={`relative py-2 px-4 font-semibold ${activeTab === 'buyer' ? 'border-b-2 border-green-600 text-green-600' : 'text-gray-500'}`}>Recebidos <TabNotificationIndicator notifications={buyerNotifications} /></button>
                <button onClick={() => setActiveTab('seller')} className={`relative py-2 px-4 font-semibold ${activeTab === 'seller' ? 'border-b-2 border-green-600 text-green-600' : 'text-gray-500'}`}>Entregues <TabNotificationIndicator notifications={sellerNotifications} /></button>
            </div>
            
            {renderContent()}
        </div>
    );
}
